import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { login, logout, verify } from '../services/api';
import { LIVE_SEARCH_ROLES } from '../config';

const AuthContext = createContext(null);

/**
 * Session state for the whole app: `status` is loading | authenticated | anonymous.
 * A 401 from any API call ("lsearch-auth-expired") signs the user out and
 * explains why on the sign-in screen.
 */
export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({ status: 'loading', user: null, message: '' });

  useEffect(() => {
    let cancelled = false;
    verify()
      .then((user) => { if (!cancelled) setState({ status: 'authenticated', user, message: '' }); })
      .catch(() => { if (!cancelled) setState({ status: 'anonymous', user: null, message: '' }); });

    const onExpired = () => {
      setState((prev) => (prev.status === 'authenticated'
        ? { status: 'anonymous', user: null, message: 'Your session expired. Please sign in again.' }
        : prev));
    };
    window.addEventListener('lsearch-auth-expired', onExpired);
    return () => {
      cancelled = true;
      window.removeEventListener('lsearch-auth-expired', onExpired);
    };
  }, []);

  const signIn = useCallback(async (username, password) => {
    await login(username, password);
    const user = await verify();
    setState({ status: 'authenticated', user, message: '' });
  }, []);

  const signOut = useCallback(async () => {
    await logout();
    setState({ status: 'anonymous', user: null, message: 'You have signed out.' });
  }, []);

  const value = useMemo(() => ({
    ...state,
    isAdmin: state.user?.role === 'admin',
    canSearchLive: LIVE_SEARCH_ROLES.includes(state.user?.role),
    signIn,
    signOut,
  }), [state, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
